/**
 * The controller: the ONLY file in `signaling/` that knows about more than one phase
 * and decides what happens after what over a session's lifetime. It does not implement
 * the connect sequence, the recovery decision tree, or teardown itself — those live in
 * their own isolated phase files (imported below) that never call each other and never
 * reach into shared state. This file is what wires them into one running session and
 * sequences them over time — first connect → active → recover/rejoin on failure → stop.
 *
 * Reading order for a newcomer:
 *   1. `connect-phase.ts`  — how a connection is actually established (start here).
 *   2. `recovery-phase.ts` — the ICE-restart-vs-rejoin decision + mechanics.
 *   3. `teardown-phase.ts` — how the media path is cleanly shut down.
 *   4. this file           — how those three get sequenced, wired to a real WebSocket
 *                            (`protoo-client.ts`), a real video pipeline
 *                            (`media/video-pipeline.ts`), and a real consumer registry
 *                            (`room-consumers.ts`), and turned into the object
 *                            `index.ts` hands to the app.
 */

import type { types as Mediasoup } from "mediasoup-client";

import { createDualVideoPipeline } from "../media/video-pipeline";
import type { SlotMetrics } from "../media/video-pipeline";
import type { VideoLatencyMode } from "../media/video-latency-control";
import { createControlAccessClient, type ControlAccessStatus } from "../control-access";
import { resolveSessionConfig } from "../sessionConfig";
import { ProtooClient } from "./protoo-client";
import { getOrCreatePeerId, makePeerId } from "./peer-id";
import { createRoomConsumers } from "./room-consumers";
import { createChatChannel } from "./chat-channel";
import { createControlChannel } from "./control-channel";
import { createVideoProducerWatchdog } from "./video-producer-watchdog";
import { runConnectPhase } from "./connect-phase";
import type { ConnectPhaseResult } from "./connect-phase";
import { runTeardownPhase } from "./teardown-phase";
import { needsRejoin, restartIce } from "./recovery-phase";
import { createSingleFlight } from "./single-flight";
import { emitStatus, type EmitStatusOpts } from "./session-status";
import { SessionPhase, STATUS_CODES } from "../status-codes";
import type { SlotStreamListener } from "../media/video-pipeline";
import type { ResolveMountSlot } from "./consumer-slots";
import type { ConnectionQualityMonitor } from "../../connection-stats";

export interface OperatorSessionConfig {
  sessionId?: string;
  signalingWsBase?: string;
  persistPeerId?: boolean;
  receiveVideo?: boolean;
  diagnosticsWire?: boolean;
  iceServers?: RTCIceServer[];
  iceTransportPolicy?: RTCIceTransportPolicy;
  iceCredentialUrl?: string;
  iceRefreshAt?: number;
  controlAuthRequired?: boolean;
  controlSid?: string;
}

export interface OperatorSessionOpts extends EmitStatusOpts {
  signal?: AbortSignal;
  appendDiag: (s: string, opts?: { level?: string }) => void;
  appendOperatorDcMessage: (text: string) => void;
  appendSocketIoDcMessage: (message: unknown) => void;
  onSignalingLost?: (detail: { code: number; reason: string; wasClean: boolean }) => void;
  onCapabilitiesRequested?: (payload: { send: (text: string) => boolean }) => void;
  onCapabilitiesStopped?: () => void;
  onControlAccess?: (status: ControlAccessStatus) => void;
  resolveMountSlot?: ResolveMountSlot | null;
  connectionQuality: ConnectionQualityMonitor;
}

/** Public video control surface: internals like `attachVideoConsumer`/`dropConsumerById`/
 * `destroy`/`getAggregateConsumerStats` are never exposed past this controller. */
export interface VideoApi {
  getSlotStream: (slot: number) => MediaStream | null;
  onSlotStream: (fn: SlotStreamListener) => () => void;
  onSlotMetrics: (fn: (slot: number, m: SlotMetrics) => void) => () => void;
  setLatencyMode: (slot: number, mode: VideoLatencyMode) => void;
  startLatencyComparison: (slot: number) => boolean;
  cancelLatencyComparison: (slot: number) => void;
  restoreVideo: () => Promise<number>;
  requestKeyframes: (slot: number) => void;
  restoreSlot: (slot: number) => boolean;
  setSlotEnabled: (slot: number, enabled: boolean) => void;
  ensureSlots: (maxSlot: number) => void;
  getSlotCount: () => number;
}

export interface OperatorSessionApi {
  authorizeControl: (key: string) => boolean;
  sendOperatorCommand: (text: string) => boolean;
  /** operator-input stream (unreliable `control` channel); false = dropped. */
  sendOperatorInput: (text: string) => boolean;
  videoApi: VideoApi;
  forceRejoin: () => Promise<void>;
  stop: () => Promise<void>;
}

export async function startOperatorSession(
  cfg: OperatorSessionConfig,
  opts: OperatorSessionOpts
): Promise<OperatorSessionApi | null> {
  const sessionId = cfg.sessionId;
  const signalingWsBase = String(cfg.signalingWsBase || "").replace(/\/+$/, "");
  if (!sessionId || !signalingWsBase) {
    opts.setStatus(STATUS_CODES.MISSING_CONFIG);
    opts.onState?.(SessionPhase.FAILED);
    return null;
  }

  const sig = (msg: string) => opts.appendDiag(`[signaling] ${msg}`);
  // Default: a fresh in-memory peerId every call — safe when the app opens more than
  // one independent connection to the same room in the same tab (nothing to collide
  // on). `persistPeerId: true` opts into the sessionStorage-cached identity instead,
  // for the single-connection-per-tab case that wants a page reload to reconnect as
  // the SAME peer rather than a new one.
  const peerId = cfg.persistPeerId ? getOrCreatePeerId(String(sessionId)) : makePeerId();

  let sessionLostNotified = false;
  const notifySessionLost = (detail: { code: number; reason: string; wasClean: boolean }) => {
    if (sessionLostNotified) return;
    sessionLostNotified = true;
    opts.onSignalingLost?.(detail);
  };

  const wsUrl = `${signalingWsBase}/?roomId=${encodeURIComponent(String(sessionId))}&peerId=${encodeURIComponent(peerId)}`;
  const protoo = new ProtooClient(wsUrl, {
    debug: (msg) => {
      if (cfg.diagnosticsWire === true) opts.appendDiag(`[wire] ${msg}`);
      else console.debug(`[protoo] ${msg}`);
    },
    logWire: cfg.diagnosticsWire === true,
  });

  const pipeline = createDualVideoPipeline({
    appendDiag: opts.appendDiag,
    protoo,
  });

  /**
   * Everything `protoo`'s six event callbacks do, grouped in one place next to where
   * `protoo` itself is created — declared here (referencing `room`/`rejoin`/`closed`/
   * etc. below via normal closure) but only CALLED once the rest of this function's
   * dependencies exist, at the bottom, right before the initial `connect()`.
   */
  function wireProtooEvents(): void {
    protoo.onRequest = async (msg) => {
      try {
        await room.handleProtooRequest(msg);
      } catch (err) {
        opts.appendDiag(`onRequest ${msg.method}: ${err}`);
        protoo.sendResponse(msg.id, false, {}, String(err), 500);
        opts.setStatus(STATUS_CODES.CONSUMER_SETUP_FAILED);
        opts.onState?.(SessionPhase.FAILED);
      }
    };

    protoo.onNotification = async (msg) => {
      const method = msg?.method || "?";
      if (
        method === "peerClosed" ||
        method === "consumerClosed" ||
        method === "dataConsumerClosed" ||
        method === "producerClosed" ||
        method === "downlinkBwe"
      ) {
        opts.appendDiag(`[protoo] notif ${method} ${JSON.stringify(msg.data || {})}`, {
          level: method === "downlinkBwe" ? "debug" : "warn",
        });
      }
      try {
        await room.handleProtooNotification(msg);
      } catch {
        opts.setStatus(STATUS_CODES.CONSUMER_NOTIF_FAILED);
        opts.onState?.(SessionPhase.FAILED);
      }
    };

    protoo.onDisconnected = () => {
      invalidateControl();
      opts.appendDiag("[protoo] WebSocket disconnected (auto-reconnect)", { level: "warn" });
      opts.setStatus(STATUS_CODES.WS_RECONNECTING);
      opts.onState?.(SessionPhase.RECONNECTING);
      pipeline.setHoldLastFrame(true);
    };

    protoo.onOpen = async ({ isReconnect }) => {
      if (!isReconnect) return;
      try {
        await rejoin();
      } catch (err) {
        opts.appendDiag(`[reconnect] onOpen rejoin failed: ${err}`, { level: "warn" });
        if (!closed && protoo.isConnected) {
          setTimeout(() => {
            void rejoin();
          }, 1500);
        }
      }
    };

    protoo.onFatalClose = ({ code, reason, wasClean }) => {
      opts.appendDiag(
        `[protoo] WebSocket fatal close code=${code} reason=${reason || "-"} clean=${wasClean}`,
        { level: "warn" }
      );
      pipeline.setHoldLastFrame(false);
      notifySessionLost({ code, reason: reason || "", wasClean });
    };

    protoo.onError = () => {
      opts.appendDiag("[protoo] WebSocket error (see console)", { level: "warn" });
    };
  }

  // --- Session-lifetime state — controller-only, no phase file ever sees these ------
  let closed = false;
  let transports: ConnectPhaseResult | null = null;
  // Rejoin can be triggered from three independent places (WS auto-reconnect's onOpen,
  // recover()'s fallback, a manual forceRejoin) — this dedupes concurrent triggers into
  // one shared run and remembers how the most recent one ended, for a caller that missed
  // it entirely (see manualForceRejoin below). NOT the same guard as `recoveringMedia`:
  // recover() can itself fall back to starting a rejoin, so the two states legitimately
  // overlap — collapsing them into one enum would misrepresent that nesting.
  const rejoinFlight = createSingleFlight<void>();
  let recoveringMedia = false;

  const recvTransportRef: { current: Mediasoup.Transport | null } = { current: null };

  const chat = createChatChannel({
    appendOperatorDcMessage: opts.appendOperatorDcMessage,
    queueWhenClosed: !cfg.controlAuthRequired,
    appendDiag: opts.appendDiag,
    opts,
  });
  const access = createControlAccessClient({
    required: cfg.controlAuthRequired === true, room: String(sessionId),
    sid: cfg.controlSid ?? "", peer: peerId,
    send: (text) => chat.send(text), onStatus: (status) => opts.onControlAccess?.(status),
  });
  access.reset();
  let iceRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  function invalidateControl() {
    if (cfg.controlAuthRequired) {
      const revoke = access.protect(JSON.stringify({ event: "control-auth-revoke", payload: { sid: cfg.controlSid } }));
      if (revoke) chat.send(revoke);
      access.reset();
      chat.close();
      control.close();
    }
  }
  const sendOperatorCommand = (text: string) => {
    const protectedText = access.protect(text);
    return protectedText !== null && chat.send(protectedText);
  };
  const control = createControlChannel({ appendDiag: opts.appendDiag });
  const sendOperatorInput = (text: string) => {
    const protectedText = access.protect(text);
    return protectedText !== null && control.send(protectedText);
  };

  const startCapabilitiesRequest = () => {
    opts.appendDiag("[config] get-capabilities → streamer", { level: "debug" });
    opts.onCapabilitiesRequested?.({
      send: (text: string) => {
        const delivered = sendOperatorCommand(text);
        if (delivered === false) {
          opts.appendDiag("[config] get-capabilities queued (chat DC not open yet)", {
            level: "debug",
          });
        }
        return delivered;
      },
    });
  };

  const onTransportFailed = (role: string) => {
    void recover({ role });
  };

  const videoWatchdog = createVideoProducerWatchdog({
    pipeline,
    appendDiag: opts.appendDiag,
    opts,
    isClosed: () => closed,
  });

  const room = createRoomConsumers({
    recvTransportRef,
    protoo,
    pipeline,
    appendDiag: opts.appendDiag,
    appendSocketIoDcMessage: (message: unknown) => {
      void access.receive(message);
      opts.appendSocketIoDcMessage(message);
    },
    setStatus: opts.setStatus,
    onTelemetryOpen: startCapabilitiesRequest,
    onTelemetryClose: () => {
      if (cfg.controlAuthRequired) access.reset();
      opts.onCapabilitiesStopped?.();
    },
    resolveMountSlot: opts.resolveMountSlot,
  });

  const ctx = {
    cfg,
    protoo,
    pipeline,
    room,
    sig,
    appendDiag: opts.appendDiag,
    opts,
    connectionQuality: opts.connectionQuality,
    recvTransportRef,
    chat,
    control,
    videoWatchdog,
    onTransportFailed,
    startCapabilitiesRequest,
  };

  /** Run the connect sequence once — the SAME function for the very first connect
   * (below, in the outer try/catch) and for every rejoin (via `rejoin()`). */
  async function connect() {
    if (cfg.iceCredentialUrl) {
      const refreshed = await resolveSessionConfig({ ...cfg, iceServers: cfg.iceServers ?? [] });
      if (closed) return;
      Object.assign(cfg, refreshed);
    }
    transports = await runConnectPhase(ctx);
    if (iceRefreshTimer) clearTimeout(iceRefreshTimer);
    if (cfg.iceCredentialUrl && cfg.iceRefreshAt) {
      iceRefreshTimer = setTimeout(() => {
        opts.appendDiag("TURN обновляется; движение остановлено. Подтвердите доступ и выберите источник заново.");
        invalidateControl();
        void manualForceRejoin().catch(() => opts.appendDiag("TURN reconnect failed", { level: "warn" }));
      }, Math.max(1000, cfg.iceRefreshAt - Date.now()));
    }
  }

  async function teardownTransports() {
    await runTeardownPhase(ctx, transports);
    transports = null;
  }

  async function runRejoinCycle(): Promise<void> {
    try {
      opts.appendDiag("[reconnect] rejoinRoom…", { level: "warn" });
      emitStatus(opts, STATUS_CODES.RECONNECTING_MEDIA, SessionPhase.RECONNECTING);
      pipeline.setHoldLastFrame(true);
      invalidateControl();
      await teardownTransports();
      await connect();
      emitStatus(opts, STATUS_CODES.REJOINED, SessionPhase.ACTIVE);
      opts.appendDiag("[reconnect] rejoinRoom ok");
      setTimeout(() => {
        void pipeline.resumeAllConsumers();
      }, 500);
    } catch (err) {
      opts.appendDiag(`[reconnect] rejoinRoom failed: ${err}`, { level: "warn" });
      emitStatus(opts, STATUS_CODES.REJOIN_FAILED, SessionPhase.FAILED);
      throw err;
    }
  }

  /**
   * Tear down and reconnect from scratch. Dedup'd via `rejoinFlight` so concurrent
   * triggers (WS auto-reconnect, `recover()`'s fallback, a manual `forceRejoin`) share
   * one cycle instead of racing several.
   */
  async function rejoin(): Promise<void> {
    if (closed) return;
    try {
      await rejoinFlight.run(runRejoinCycle);
    } finally {
      pipeline.scheduleReleaseHoldLastFrame(8000);
    }
  }

  /**
   * Triggered when a transport's own `connectionstatechange` fires "failed" (see
   * `onTransportFailed` above, wired into `connect-phase.ts` via `ctx`). Tries the
   * cheapest fix first (ICE restart on just the broken transport) and only falls back
   * to a full rejoin if that's not enough.
   */
  async function recover(detail: { role?: string } = {}): Promise<void> {
    if (closed || recoveringMedia) return;
    invalidateControl();
    recoveringMedia = true;
    try {
      const role = detail.role ?? "recv";
      opts.appendDiag(`[reconnect] recover role=${role}`, { level: "warn" });
      if (rejoinFlight.promise) {
        opts.appendDiag("[reconnect] recover: rejoin already in flight", {
          level: "debug",
        });
        await rejoinFlight.promise;
        return;
      }
      if (!protoo?.isConnected) {
        opts.appendDiag("[reconnect] protoo down, waiting for WS auto-reconnect", {
          level: "debug",
        });
        return;
      }
      if (cfg.controlAuthRequired || cfg.iceCredentialUrl || needsRejoin(transports)) {
        await reconnectSocketThenRejoin();
        return;
      }
      const { recv } = await restartIce(ctx, transports);
      // recv carries video — a send-only restart still leaves the video path broken,
      // so only recv success counts as "media recovered" here; anything else falls
      // back to a full rejoin rather than reporting a false recovery.
      if (recv) {
        emitStatus(opts, STATUS_CODES.ICE_RESTORED, SessionPhase.ACTIVE);
        setTimeout(() => {
          void pipeline.resumeAllConsumers();
        }, 500);
        return;
      }
      // Same reason as the `needsRejoin` branch above: this connection has already
      // joined the room, so a plain `rejoin()` on it would hit the same guaranteed
      // server-side close.
      await reconnectSocketThenRejoin();
    } catch (err) {
      opts.appendDiag(`[reconnect] recover failed: ${err}`, { level: "warn" });
    } finally {
      recoveringMedia = false;
    }
  }

  /**
   * Force a fresh WebSocket connection and let the resulting
   * `protoo.onOpen({isReconnect:true})` (wired below) drive the actual rejoin — never
   * attempt `rejoin()` directly on the CURRENT connection, even while
   * `protoo.isConnected` still reports it open. Observed against the real signaling
   * server: it closes that connection outright (WS close code=4000 "closed by
   * protoo-server") the instant a `join` request arrives for a peer that already joined
   * the room on it — right in the middle of `runConnectPhase`'s own `join` call. So
   * reusing an already-joined connection for a rejoin isn't a rare race, it's a
   * guaranteed, self-inflicted `REJOIN_FAILED`/`SessionPhase.FAILED` blip (visible as
   * the connection splash's "Ошибка подключения") on every single attempt, which then
   * only recovers because the WS's own auto-reconnect happens to retry a moment later
   * on a genuinely fresh connection. Forcing that reconnect FIRST sidesteps the doomed
   * attempt entirely instead of recovering from it after the fact.
   */
  async function reconnectSocketThenRejoin(): Promise<void> {
    // Register the waiter BEFORE triggering the reconnect: `await` always yields to the
    // microtask queue at least once, so the eventual onOpen → rejoin() chain triggered
    // by reconnectNow() below can never finish before this line runs, no matter how
    // "synchronous" the underlying WebSocket double behaves — there is no race to poll
    // for here, real (async) reconnect and a same-tick test double both just resolve
    // this promise once the cycle they started finishes.
    const nextRejoin = rejoinFlight.waitForNext();
    protoo.reconnectNow();
    const timedOut = Symbol("rejoin-timeout");
    const outcome = await Promise.race([
      nextRejoin.then(() => "done" as const),
      new Promise<typeof timedOut>((resolve) => setTimeout(() => resolve(timedOut), 20_000)),
    ]);
    if (outcome === timedOut) {
      // Last-resort safety net: the socket did come back but, for whatever reason,
      // never triggered a rejoin on its own — self-trigger one rather than declaring
      // failure while actually connected.
      if (protoo.isConnected && !rejoinFlight.promise) {
        await rejoin();
        return;
      }
      throw new Error("rejoin timeout");
    }
    if (rejoinFlight.lastError) throw rejoinFlight.lastError;
  }

  /**
   * Manual reconnect ("Переподключить" button). Always goes through
   * `reconnectSocketThenRejoin()` — see its doc comment for why reusing the current
   * connection (even when nominally still open) isn't safe. Never starts a second
   * rejoin cycle in parallel with the automatic one — `protoo.onOpen({isReconnect:true})`
   * is the sole trigger for the actual rejoin once the socket reopens; racing a second
   * `rejoin()` against it would duplicate the whole join sequence (double
   * get-capabilities, robot-config reset mid-flight, ...) — `reconnectSocketThenRejoin`
   * already keeps that guarantee via `rejoinFlight`.
   */
  async function manualForceRejoin(): Promise<void> {
    invalidateControl();
    await reconnectSocketThenRejoin();
  }

  async function stop(): Promise<void> {
    invalidateControl();
    closed = true;
    if (iceRefreshTimer) clearTimeout(iceRefreshTimer);
    await teardownTransports();
    try {
      pipeline?.destroy();
    } catch {
      /* ignore */
    }
    try {
      protoo?.close();
    } catch {
      /* ignore */
    }
  }

  wireProtooEvents();

  opts.signal?.addEventListener(
    "abort",
    () => {
      void stop();
    },
    { once: true }
  );

  pipeline.startStatsPolling();

  opts.appendDiag(`[build] operator-ui v${__APP_VERSION__}`);
  sig(`0/7 start room="${sessionId}" peer=${peerId} → ${signalingWsBase}`);
  opts.setStatus(STATUS_CODES.SIGNALING_CONNECTING);
  opts.onState?.(SessionPhase.CONNECTING);
  try {
    await protoo.connect({ signal: opts.signal });
    sig("1/7 WebSocket (protoo) open");
    opts.setStatus(STATUS_CODES.MEDIASOUP_LOADING);

    await connect();
    opts.setStatus(STATUS_CODES.SESSION_ACTIVE);
    opts.onState?.(SessionPhase.ACTIVE);
  } catch (err) {
    // Without this, a failed initial connect/join leaves the WS + partially-built
    // transports alive and still wired to `opts` — the caller (createTeleopSession's
    // start()) has already rejected and given up, but this orphaned connection keeps
    // running its own auto-reconnect/rejoin machinery in the background, silently
    // re-emitting state/status into the SAME emitter the caller stopped listening to
    // as "done". Since nothing ever assigns the returned facade in that case, the host
    // app has no handle to it either — an unkillable zombie session. Tear it down here
    // so a failed start() really is fully stopped, not just abandoned.
    opts.appendDiag(`[startup] failed: ${err}`, { level: "warn" });
    await stop();
    throw err;
  }

  return {
    authorizeControl: access.authorize,
    sendOperatorCommand,
    sendOperatorInput,
    videoApi: {
      getSlotStream: (slot) => pipeline.getSlotStream(slot),
      onSlotStream: (fn) => pipeline.onSlotStream(fn),
      onSlotMetrics: (fn) => pipeline.onSlotMetrics(fn),
      setLatencyMode: (slot, mode) => pipeline.setLatencyMode(slot, mode),
      startLatencyComparison: (slot) => pipeline.startLatencyComparison(slot),
      cancelLatencyComparison: (slot) => pipeline.cancelLatencyComparison(slot),
      restoreVideo: async () => pipeline.resumeAllConsumers(),
      requestKeyframes: (slot) => pipeline.requestKeyframes(slot),
      restoreSlot: (slot) => pipeline.restoreSlot(slot),
      setSlotEnabled: (slot, enabled) => pipeline.setSlotEnabled(slot, enabled),
      ensureSlots: (maxSlot) => pipeline.ensureSlots?.(maxSlot),
      getSlotCount: () => pipeline.getSlotCount?.() ?? 2,
    },
    forceRejoin: () => manualForceRejoin(),
    stop,
  };
}
